import { useEffect, useState } from 'react'

import { useKeenSlider } from 'keen-slider/react'

import { useGuideContext } from 'react-provider'
import CardLayout from '@/components/CardLayout'
import GroupItem from './components/GroupItem'
import { isSuccess } from '@/axios'
import { getDiscoverGroup, IDiscoverGroupItem, postGroupJoin, getGroupCheckInGroups } from '@/api'
import { EConversationType } from '@/types'
import { store, loginStateAtom, mobileStatusAtom } from '@/store'

import { cn, customJumpHref, jumpMainLogin } from '@/utils'
import { useDebounceFn } from 'ahooks'
import { Toast } from 'antd-mobile'
import { useAtomValue } from 'jotai'
import SkeletonState from '@/components/CommonState/SkeletonState'

interface PropsIn {
  className?: string
}

const DiscoverGroup = (props: PropsIn) => {
  const { className } = props
  const [data, setData] = useState<IDiscoverGroupItem[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const isLogin = store.get(loginStateAtom)
  const isMobile = useAtomValue(mobileStatusAtom)
  const [sliderRef, instanceRef] = useKeenSlider({
    slides: {
      perView: isMobile
        ? 'auto'
        : () => {
            const rootFontSize = document.documentElement.style.fontSize.split('px')[0]
            const containerClientWidth = document.querySelector('#discoverGroupBody')?.clientWidth
            const remSize = Number((Math.ceil(Number(rootFontSize) * 100) / 100).toFixed(1))

            const itemW = remSize * 29.9 + remSize * 2.2

            const bodyW = containerClientWidth || remSize * 63.1

            return bodyW / itemW
          },
      spacing: () => {
        const rootFontSize = document.documentElement.style.fontSize.split('px')[0]

        const remSize = (Math.ceil(Number(rootFontSize) * 100) / 100).toFixed(1)
        return Number(remSize) * 2.2
      },
    },
  })

  const { reportReady } = useGuideContext()

  const getData = async () => {
    setIsLoading(true)

    const resp = await getDiscoverGroup()

    if (!isSuccess(resp)) {
      setIsLoading(false)
      return
    }

    const list = resp.data.list

    if (!list.length || !isLogin) {
      setData(list)
      setIsLoading(false)
      return
    }

    try {
      const groupIds = list.map((item) => item.group_id)
      const resp2 = await getGroupCheckInGroups({ group_ids: groupIds })

      if (isSuccess(resp2)) {
        const mergedList = list.map((item) => {
          const status = resp2.data.list.find((item2) => item2.group_id === item.group_id)
          if (status) {
            item.in_group = status.in_group
          }
          return item
        })
        setData(mergedList)
      } else {
        setData(list)
      }
    } catch {
      setData(list)
    } finally {
      setIsLoading(false)
    }
  }

  const openChat = (data: IDiscoverGroupItem) => {
    const conversation = {
      id: '',
      session_type: EConversationType.GROUP,
      session_id: data.group_id,
      session_name: data.group_info?.name as string,
      session_avatar_url: data.group_info?.avatar as string,
      last_message_type: 0,
      last_message_content: '',
      last_message_created_at: 0,
      last_read_at: null,
      last_message_sender_id: '',
    }
    const encodedData = encodeURIComponent(JSON.stringify(conversation))
    // handleImGroupOpenTrack(
    //   params.type,
    //   params.id,
    //   novelInfo.tag_id,
    //   String(data.group_code)
    // )
    customJumpHref(`/message?jumpNow=1&chat=${encodedData}`)
  }

  const handleJoin = async (group: IDiscoverGroupItem) => {
    if (!isLogin) {
      await new Promise<void>((resolve) => {
        Toast.show({
          position: 'top',
          content: 'Login to continue',
          duration: 1500,
          afterClose: resolve,
        })
      })
      return jumpMainLogin()
    }
    const resp = await postGroupJoin({ group_id: String(group.group_id) })

    if (isSuccess(resp)) {
      openChat(group)
    }
  }

  useEffect(() => {
    if (instanceRef.current && data) {
      instanceRef.current?.update()
    }
  }, [data])

  useEffect(() => {
    getData()
  }, [])

  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        getData()
      }
    }

    window.addEventListener('pageshow', handlePageShow)

    return () => {
      window.removeEventListener('pageshow', handlePageShow)
    }
  }, [])

  const { run: handleResize } = useDebounceFn(
    () => {
      instanceRef.current?.update()
    },
    { wait: 500 },
  )

  useEffect(() => {
    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  if (isLoading) {
    return (
      <CardLayout title='Group' rootClassName={cn(className)}>
        <SkeletonState type={['group']} />
      </CardLayout>
    )
  }

  return (
    <>
      {data?.length ? (
        <CardLayout
          title='Group'
          rootClassName={cn(className)}
          ref={() => {
            reportReady('groupBox')
          }}
        >
          <div
            className={cn(
              'relative w-full overflow-hidden px-[30px] md:px-0',
              `before:absolute before:left-0 before:top-0 before:z-[1] before:block before:h-full before:w-[30px] before:bg-base-color before:content-[''] md:before:w-0`,
            )}
            id='discoverGroupBody'
          >
            <div ref={sliderRef} className={cn('keen-slider w-full !overflow-visible')}>
              {data?.map((item, index) => {
                if (item.group_info) {
                  const bgClass =
                    index % 3 === 0
                      ? 'bg-discover-group-bg-color-1'
                      : index % 3 === 1
                        ? 'bg-discover-group-bg-color-2'
                        : 'bg-discover-group-bg-color-3'
                  return (
                    <div className={cn('keen-slider__slide', '!min-w-[299px] !max-w-[299px]')} key={`${item.group_id}-${index}`}>
                      <GroupItem
                        data={item}
                        className={bgClass}
                        handleJoin={async () => await handleJoin(item)}
                        openChat={() => {
                          openChat(item)
                        }}
                      />
                    </div>
                  )
                } else {
                  return null
                }
              })}
            </div>
          </div>
        </CardLayout>
      ) : null}
    </>
  )
}

export default DiscoverGroup
